import React, { useState } from 'react';
import { Card, Form, InputNumber, Select, Radio, Row, Col, Switch, Slider, Divider } from 'antd';

export interface AnimatedPatternPointsConfig {
    circleSize: number;
    colorScheme: string;
    invertColors: boolean;
    resolution: 'hour' | 'day' | 'week' | 'month';
    lookbackDays: number;
    pointOpacity: number;
    templateOpacity: number;
    showLabels: boolean;
    labelFontSize: number;
    valueRangeMode: 'auto' | 'fixed' | 'areaDefault';
    valueMin: number | null;
    valueMax: number | null;
}

const DEFAULT_CONFIG: AnimatedPatternPointsConfig = {
    circleSize: 20,
    colorScheme: 'blue-red',
    invertColors: false,
    resolution: 'day',
    lookbackDays: 30,
    pointOpacity: 100,
    templateOpacity: 50,
    showLabels: false,
    labelFontSize: 10,
    valueRangeMode: 'auto',
    valueMin: null,
    valueMax: null,
};

interface Props {
    initialConfig?: Partial<AnimatedPatternPointsConfig>;
    onChange: (config: AnimatedPatternPointsConfig) => void;
}

export const SectionConfigAnimatedPatternPoints: React.FC<Props> = ({ initialConfig, onChange }) => {
    const [config, setConfig] = useState<AnimatedPatternPointsConfig>({
        ...DEFAULT_CONFIG,
        ...initialConfig,
    });

    const updateField = (field: keyof AnimatedPatternPointsConfig, value: any) => {
        const newConfig = { ...config, [field]: value };
        setConfig(newConfig);
        onChange(newConfig);
    };

    return (
        <Card size="small" style={{ background: '#f5f5f5' }}>
            <Divider orientation="left">Time Settings</Divider>
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Form.Item label="Time Resolution">
                        <Select
                            value={config.resolution}
                            onChange={(val) => updateField('resolution', val)}
                            options={[
                                { label: 'Hourly', value: 'hour' },
                                { label: 'Daily', value: 'day' },
                                { label: 'Weekly', value: 'week' },
                                { label: 'Monthly', value: 'month' },
                            ]}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Lookback Period (days)">
                        <InputNumber
                            min={1}
                            max={365}
                            value={config.lookbackDays}
                            onChange={(val) => updateField('lookbackDays', val || 30)}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left">Visualization</Divider>
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Form.Item label="Circle Size (pixels)">
                        <InputNumber
                            min={10}
                            max={50}
                            value={config.circleSize}
                            onChange={(val) => updateField('circleSize', val || 20)}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Color Scheme">
                        <Select
                            value={config.colorScheme}
                            onChange={(val) => updateField('colorScheme', val)}
                            options={[
                                { label: 'Blue → Red', value: 'blue-red' },
                                { label: 'Red → Green', value: 'green-red' },
                                { label: 'Red → Yellow → Green', value: 'red-yellow-green' },
                                { label: 'Yellow → Red', value: 'yellow-red' },
                                { label: 'Viridis', value: 'viridis' },
                            ]}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Invert Colors">
                        <Switch
                            checked={config.invertColors}
                            onChange={(checked) => updateField('invertColors', checked)}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Form.Item label="Show Point Labels">
                        <Switch
                            checked={config.showLabels}
                            onChange={(checked) => updateField('showLabels', checked)}
                        />
                    </Form.Item>
                </Col>
                {config.showLabels && (
                    <Col span={12}>
                        <Form.Item label="Label Font Size">
                            <InputNumber
                                min={6}
                                max={24}
                                value={config.labelFontSize}
                                onChange={(val) => updateField('labelFontSize', val || 10)}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                )}
            </Row>

            <Divider orientation="left">Opacity Controls</Divider>
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Form.Item label={`Template Opacity: ${config.templateOpacity}%`}>
                        <Slider
                            min={0}
                            max={100}
                            value={config.templateOpacity}
                            onChange={(val) => updateField('templateOpacity', val)}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label={`Point Opacity: ${config.pointOpacity}%`}>
                        <Slider
                            min={0}
                            max={100}
                            value={config.pointOpacity}
                            onChange={(val) => updateField('pointOpacity', val)}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left">Value Range</Divider>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label="Value Range Mode">
                        <Select
                            value={config.valueRangeMode}
                            onChange={(val) => updateField('valueRangeMode', val)}
                            options={[
                                { label: 'Auto', value: 'auto' },
                                { label: 'Fixed', value: 'fixed' },
                                { label: 'Area default (metric color rule)', value: 'areaDefault' },
                            ]}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
                {config.valueRangeMode === 'fixed' && (
                    <>
                        <Col span={8}>
                            <Form.Item label="Min Value">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={config.valueMin ?? undefined}
                                    onChange={(val) => updateField('valueMin', val)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Max Value">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={config.valueMax ?? undefined}
                                    onChange={(val) => updateField('valueMax', val)}
                                />
                            </Form.Item>
                        </Col>
                    </>
                )}
            </Row>
        </Card>
    );
};

export default SectionConfigAnimatedPatternPoints;
